import { LanguageCode, DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, removeLanguagePrefix, translatePath } from './languageUtils';

/**
 * Error class for SEO utility validation failures
 */
class SEOValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SEOValidationError';
  }
}

/**
 * Validate that a string is a valid pathname
 * @param pathname - String to validate
 * @returns True if valid pathname
 */
const isValidPathname = (pathname: string): boolean => {
  return typeof pathname === 'string' && pathname.length > 0 && pathname.startsWith('/');
};

/**
 * Validate that a string is a valid LanguageCode
 * @param language - String to validate
 * @returns True if valid LanguageCode
 */
const isValidLanguageCode = (language: string): boolean => {
  return SUPPORTED_LANGUAGES.includes(language as LanguageCode);
};

/**
 * Validate that a string is a valid URL format with security checks (internal utility)
 * @param url - String to validate
 * @returns True if valid URL format and secure
 * @throws SEOValidationError for invalid URLs with descriptive message
 */
const validateUrlFormat = (url: string): void => {
  if (typeof url !== 'string' || url.length === 0) {
    throw new SEOValidationError('URL must be a non-empty string');
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url);
  } catch {
    throw new SEOValidationError(`Invalid URL format: ${url}`);
  }
  
  // Only allow HTTP and HTTPS protocols
  if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
    throw new SEOValidationError(`Invalid protocol "${parsedUrl.protocol}". Only HTTP and HTTPS are allowed`);
  }
  
  // In production, disallow localhost URLs for security
  if (import.meta.env.PROD && (
    parsedUrl.hostname === 'localhost' || 
    parsedUrl.hostname === '127.0.0.1' ||
    parsedUrl.hostname.startsWith('192.168.') ||
    parsedUrl.hostname.startsWith('10.') ||
    parsedUrl.hostname.startsWith('172.')
  )) {
    throw new SEOValidationError('Localhost and private network URLs are not allowed in production');
  }
};

/**
 * Check if URL is valid (user-facing utility)
 * @param url - String to validate
 * @returns True if valid URL format and secure, false otherwise
 */
const isValidUrl = (url: string): boolean => {
  try {
    validateUrlFormat(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Generate proper canonical URL respecting language structure and duplicate route consolidation
 * @param pathname - Current pathname (e.g., '/es/registrarse' or '/en/register')
 * @param language - Current language code ('es' | 'en')
 * @param siteUrl - Base site URL (e.g., 'https://alertalegal.co')
 * @param isCanonicalRoute - Whether this is the canonical version of duplicate routes
 * @returns Absolute canonical URL
 */
export const generateCanonicalUrl = (
  pathname: string,
  language: LanguageCode,
  siteUrl: string,
  isCanonicalRoute: boolean = true
): string => {
  // Input validation
  if (!isValidPathname(pathname)) {
    throw new SEOValidationError('pathname must be a non-empty string starting with "/"');
  }
  if (!isValidLanguageCode(language)) {
    throw new SEOValidationError(`language must be one of: ${SUPPORTED_LANGUAGES.join(', ')}`);
  }
  validateUrlFormat(siteUrl);
  // Use safe language removal function instead of vulnerable regex
  const pathWithoutLang = removeLanguagePrefix(pathname);
  
  // For duplicate routes (e.g., /register vs /registrarse), always point to canonical (Spanish) version
  if (!isCanonicalRoute) {
    return `${siteUrl}/${DEFAULT_LANGUAGE}${pathWithoutLang}`;
  }
  
  // For canonical routes, use the current language
  return `${siteUrl}/${language}${pathWithoutLang}`;
};

/**
 * Generate hreflang tags with required self-reference and language alternatives
 * Properly maps routes between languages (e.g., /politica-de-privacidad -> /privacy-policy)
 * @param pathname - Current pathname (e.g., '/es/registrarse')
 * @param siteUrl - Base site URL (e.g., 'https://alertalegal.co')
 * @param currentLanguage - Current page language for self-referencing
 * @returns Object with language codes as keys and absolute URLs as values
 */
export const generateHreflangTags = (
  pathname: string,
  siteUrl: string,
  currentLanguage: LanguageCode
): Record<string, string> => {
  // Input validation
  if (!isValidPathname(pathname)) {
    throw new SEOValidationError('pathname must be a non-empty string starting with "/"');
  }
  validateUrlFormat(siteUrl);
  if (!isValidLanguageCode(currentLanguage)) {
    throw new SEOValidationError(`currentLanguage must be one of: ${SUPPORTED_LANGUAGES.join(', ')}`);
  }
  // Use safe language removal function instead of vulnerable regex
  const basePath = removeLanguagePrefix(pathname);
  
  // Generate hreflang tags with proper path translation for each language
  const hreflangTags = SUPPORTED_LANGUAGES.reduce((tags, lang) => {
    // Translate the path from current language to target language
    const translatedPath = translatePath(basePath, currentLanguage, lang);
    tags[lang] = `${siteUrl}/${lang}${translatedPath}`;
    return tags;
  }, {} as Record<string, string>);
  
  // Add x-default pointing to default language (Spanish) as per Google guidelines
  const defaultPath = translatePath(basePath, currentLanguage, DEFAULT_LANGUAGE);
  hreflangTags['x-default'] = `${siteUrl}/${DEFAULT_LANGUAGE}${defaultPath}`;
  
  return hreflangTags;
};

/**
 * Get language alternatives for the current path with proper route mapping
 * @param pathname - Current pathname (e.g., '/es/registrarse')
 * @param siteUrl - Base site URL
 * @returns Array of language alternative objects with lang and href
 */
export const getLanguageAlternatives = (
  pathname: string,
  siteUrl: string,
  currentLanguage: LanguageCode = DEFAULT_LANGUAGE
): Array<{ lang: string; href: string }> => {
  // Input validation
  if (!isValidPathname(pathname)) {
    throw new SEOValidationError('pathname must be a non-empty string starting with "/"');
  }
  validateUrlFormat(siteUrl);
  if (!isValidLanguageCode(currentLanguage)) {
    throw new SEOValidationError(`currentLanguage must be one of: ${SUPPORTED_LANGUAGES.join(', ')}`);
  }
  
  const hreflangTags = generateHreflangTags(pathname, siteUrl, currentLanguage);
  
  return Object.entries(hreflangTags).map(([lang, href]) => ({
    lang,
    href
  }));
};

/**
 * Check if the current URL uses HTTPS protocol (required for canonical URLs)
 * @param url - URL to check
 * @returns True if URL uses HTTPS
 */
export const isHttpsUrl = (url: string): boolean => {
  // Input validation
  if (typeof url !== 'string' || url.length === 0) {
    return false;
  }
  
  // Validate URL format first
  if (!isValidUrl(url)) {
    return false;
  }
  
  return url.startsWith('https://');
};

/**
 * Ensure URL uses HTTPS protocol for canonical URLs (Google requirement)
 * @param url - URL to convert
 * @returns URL with HTTPS protocol
 */
export const ensureHttpsUrl = (url: string): string => {
  // Input validation
  if (typeof url !== 'string' || url.length === 0) {
    throw new SEOValidationError('url must be a non-empty string');
  }
  
  // Validate URL format before processing
  validateUrlFormat(url);
  
  // Only process HTTP/HTTPS URLs
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    throw new SEOValidationError('url must use http:// or https:// protocol');
  }
  
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};